/**
 * Helpers for rendering candidate-anonymization placeholders in the viewer's language.
 *
 * At anonymization time the backend WRITES literal placeholder strings into the database
 * (candidate names and event/interview/calendar titles). These persist even after de-anonymize,
 * so the only way to localize them is at render time. Candidate names are detected via the
 * structured `Candidate.anonymizedAt` field; event/interview titles have no structured flag, so
 * they are detected by matching the redaction sentinel below.
 */

/**
 * The exact sentinel the backend writes into event/interview/calendar titles at anonymization
 * time (mirrors ComplianceManager.ANONYMIZATION_REDACT_MESSAGE). This is the ONE place this
 * literal lives in the frontend.
 */
export const ANONYMIZATION_REDACT_MESSAGE = '***REDACTED VIA CANDIDATE ANONYMIZATION***';

/**
 * Returns a localized "Anonymized" label when a candidate is anonymized (its `anonymizedAt` is
 * set), otherwise the candidate's name unchanged.
 *
 * @param name - The candidate name as stored (already the literal "Anonymized" in the DB when the
 *   candidate is anonymized; localized here so non-English sessions don't see English).
 * @param anonymizedAt - The candidate's `anonymizedAt` timestamp (null/undefined when not anonymized).
 * @param t - A translator function (namespace:key → localized string).
 */
export const localizeAnonymizedName = (
  name: string | null | undefined,
  anonymizedAt: string | null | undefined,
  t: (key: string) => string
): string => (anonymizedAt ? t('hiring-dashboard-candidates:candidates.anonymize.anonymizedName') : (name ?? ''));

/**
 * Returns a localized "Redacted" label when a value equals the anonymization redaction sentinel,
 * otherwise the value unchanged.
 *
 * @param value - A title/text value that may have been redacted at anonymization time.
 * @param t - A translator function (namespace:key → localized string).
 */
export const localizeRedacted = (value: string | null | undefined, t: (key: string) => string): string =>
  value === ANONYMIZATION_REDACT_MESSAGE
    ? t('hiring-dashboard-candidates:candidates.anonymize.redactedTitle')
    : (value ?? '');
